import { useMemo } from 'react';
import { lockDateKey } from '@/services/lockCalendarDates';
import { diffCalendarDays, getBusinessDateString, parseAsLocalDate } from '@/utils/dateHelpers';
import type { ProspectLoan } from '../types';
import { INVESTOR_LOAN_NUMBER_MAX_LENGTH } from './hooks/usePricingDraft';

interface LockDetailsCardProps {
  loan: ProspectLoan;
  currentLoan?: ProspectLoan;
  diffClass: (snapshotValue: unknown, currentValue: unknown) => string;
  /** The number to show: the lock's copy in lock history, otherwise the draft. */
  investorLoanNumber: string;
  investorLoanNumberError: string | null;
  investorLoanNumberReadOnly: boolean;
  onInvestorLoanNumberChange: (value: string) => void;
}

const INVESTOR_LOAN_NUMBER_ERROR_ID = 'pv2-investor-loan-number-error';

function formatDate(value: string | undefined | null): string {
  if (!value) {
    return '—';
  }
  const d = parseAsLocalDate(value);
  return d ? d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';
}

// MM/DD/YYYY for a YYYY-MM-DD calendar key, with no time zone conversion.
function formatCalendarKey(key: string): string {
  return `${key.slice(5, 7)}/${key.slice(8, 10)}/${key.slice(0, 4)}`;
}

/**
 * Lock Details — `.panel` with `.lock-timeline` rail showing Lock Date, Today
 * (accent dot + glow), and Expiration. `.lt-meta` row underneath carries the
 * Investor Loan # + Active status pill. The number is part of the page's
 * Save changes: the workspace owns its value, validation and saving.
 */
export function LockDetailsCard({
  loan,
  currentLoan,
  diffClass,
  investorLoanNumber,
  investorLoanNumberError,
  investorLoanNumberReadOnly,
  onInvestorLoanNumberChange
}: LockDetailsCardProps) {
  const lockDate = loan.rate_lock_buy_side_lock_date;
  const expiresDate = loan.rate_lock_buy_side_lock_expires;
  const term = loan.rate_lock_buy_side_number_of_days;
  const isLocked = loan.rate_lock_status === 'Locked';

  // Calendar days on Pacific dates: today is the Pacific business date, and
  // flooring the milliseconds between local midnights loses a day across DST.
  const timeline = useMemo(() => {
    const lockKey = lockDateKey(lockDate);
    const expiresKey = lockDateKey(expiresDate);
    if (!lockKey || !expiresKey) {
      return null;
    }
    const todayKey = getBusinessDateString();
    const total = diffCalendarDays(lockKey, expiresKey);
    if (total <= 0) {
      return null;
    }
    const elapsed = Math.max(0, Math.min(total, diffCalendarDays(lockKey, todayKey)));
    const pct = Math.round((elapsed / total) * 10000) / 100;
    const daysRemaining = Math.max(0, diffCalendarDays(todayKey, expiresKey));
    return { todayKey, total, elapsedPct: pct, daysRemaining };
  }, [lockDate, expiresDate]);

  return (
    <div className="panel">
      <div className="panel-hdr">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <h3>Lock Details</h3>
      </div>
      <div className="panel-body">
        <div className="lock-timeline">
          <div className="lt-term-header">
            <span className="lt-term-lbl">Lock Term</span>
            <span className="lt-term-val">{term !== undefined && term !== null ? `${term} days` : '—'}</span>
          </div>
          {timeline ? (
            <div className="lt-rail">
              <div className="lt-track" />
              <div className="lt-elapsed" style={{ width: `${timeline.elapsedPct}%` }} />
              <div className="lt-marker lt-start" style={{ left: '0%' }}>
                <div className="lt-marker-dot" />
                <div className="lt-marker-label">
                  <div className="lt-marker-lbl">Lock Date</div>
                  <div
                    className={`lt-marker-val${
                      diffClass(loan.rate_lock_buy_side_lock_date, currentLoan?.rate_lock_buy_side_lock_date) ? ' text-red-600' : ''
                    }`}
                  >
                    {formatDate(lockDate)}
                  </div>
                </div>
              </div>
              {timeline.elapsedPct > 3 && timeline.elapsedPct < 97 && (
                <div className="lt-marker lt-today" style={{ left: `${timeline.elapsedPct}%` }}>
                  <div className="lt-marker-dot today" />
                  <div className="lt-marker-label">
                    <div className="lt-marker-lbl">Today</div>
                    <div className="lt-marker-val">{formatCalendarKey(timeline.todayKey)}</div>
                    <div className="lt-days-remaining">{timeline.daysRemaining} days remaining</div>
                  </div>
                </div>
              )}
              <div className="lt-marker lt-end" style={{ left: '100%' }}>
                <div className="lt-marker-dot" />
                <div className="lt-marker-label right">
                  <div className="lt-marker-lbl">Expiration</div>
                  <div
                    className={`lt-marker-val${
                      diffClass(loan.rate_lock_buy_side_lock_expires, currentLoan?.rate_lock_buy_side_lock_expires) ? ' text-red-600' : ''
                    }`}
                  >
                    {formatDate(expiresDate)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '12px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: '12px' }}>
              Lock date &amp; expiration not set.
            </div>
          )}

          <div className="lt-meta">
            <div className={`lt-meta-item${investorLoanNumberError ? ' has-error' : ''}`}>
              <span className="lt-meta-lbl">Investor Loan #</span>
              <div
                className="field-edit inline"
                style={{ flex: '0 1 180px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <input
                  type="text"
                  style={{ width: '100%', minWidth: 0 }}
                  value={investorLoanNumber}
                  onChange={(e) => onInvestorLoanNumberChange(e.target.value)}
                  disabled={investorLoanNumberReadOnly}
                  maxLength={INVESTOR_LOAN_NUMBER_MAX_LENGTH}
                  placeholder="—"
                  aria-label="Investor Loan #"
                  aria-invalid={investorLoanNumberError !== null}
                  aria-describedby={investorLoanNumberError ? INVESTOR_LOAN_NUMBER_ERROR_ID : undefined}
                />
              </div>
              {investorLoanNumberError && (
                <div id={INVESTOR_LOAN_NUMBER_ERROR_ID} className="lt-meta-error" role="alert">
                  {investorLoanNumberError}
                </div>
              )}
            </div>
            {isLocked && (
              <div className="lt-meta-item">
                <span className="lt-meta-lbl">Status</span>
                <span className="lt-status-pill">
                  <span className="lt-status-dot" />
                  Active
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
